"use client";

import { useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useTranslations, useLocale } from "next-intl";
import { Card, CardContent } from "../ui/card";
import moment from "moment-timezone";

// Composant pour un assignement principal
const MainAssignmentCard = ({ assignment, userTimezone }) => {
    const t = useTranslations("AssignmentSection");
    const locale = useLocale();
    const course = assignment.course;
    const team = assignment.team;
    const dueDate = assignment.dueDate;
    const progress = assignment.progress || 0;
    const partNumber = course?.partNumber || 1;
    const sessionPosition = course?.sessionPosition || 1;

    const isOverdue =
        dueDate && progress < 100 && moment().tz(userTimezone).isAfter(moment(dueDate));
    const isDueSoon =
        dueDate &&
        !isOverdue &&
        progress < 100 &&
        moment(dueDate).diff(moment().tz(userTimezone), "days") <= 3;
    const isCompleted = progress === 100;

    return (
        <Link href={`/course/${course?.slug}/${partNumber}/${sessionPosition}`} className="group block">
            <Card className={`special-card ${isCompleted ? "overflow-hidden" : ""}`}>
                <CardContent className="pt-5 relative">
                    {isCompleted && (
                        <div
                            className="absolute inset-0 pointer-events-none opacity-[0.03]"
                            style={{
                                backgroundImage: "repeating-linear-gradient(45deg, currentColor, currentColor 1px, transparent 1px, transparent 8px)"
                            }}
                        />
                    )}
                    <div className="flex items-center gap-4 mb-4 relative">
                        <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0">
                            {course?.image ? (
                                <Image
                                    src={course.image}
                                    alt={course?.name || t("unknownCourse")}
                                    width={48}
                                    height={48}
                                    className="object-cover w-full h-full"
                                />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">
                                        {course?.name?.charAt(0) || "?"}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-lg leading-tight group-hover:text-black group-hover:dark:text-white transition-colors">
                                {course?.name || t("unknownCourse")}
                            </h3>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {/* Team info */}
                                <div className="flex items-center gap-1.5">
                                    {team?.logo ? (
                                        <Image
                                            src={team.logo}
                                            alt={team.name}
                                            width={16}
                                            height={16}
                                            className="rounded object-cover"
                                        />
                                    ) : (
                                        <div className="w-4 h-4 bg-primary/20 rounded flex items-center justify-center">
                                            <span className="text-[8px] font-bold text-primary">
                                                {team?.name?.charAt(0)?.toUpperCase() || "T"}
                                            </span>
                                        </div>
                                    )}
                                    <span className="text-sm text-gray-500 dark:text-grayDark-400">
                                        {team?.name}
                                    </span>
                                </div>
                                <span className="text-gray-300 dark:text-grayDark-600">
                                    •
                                </span>
                                <span className="text-sm text-gray-500 dark:text-grayDark-400">
                                    {progress}%
                                </span>
                                {dueDate && (
                                    <>
                                        <span className="text-gray-300 dark:text-grayDark-600">
                                            •
                                        </span>
                                        <span
                                            className={`text-sm font-medium ${
                                                isOverdue
                                                    ? "text-red-500"
                                                    : isDueSoon
                                                    ? "text-orange-500"
                                                    : "text-gray-500 dark:text-grayDark-400"
                                            }`}
                                        >
                                            {isOverdue
                                                ? t("overdue")
                                                : t("dueDate", {
                                                      date: moment(dueDate)
                                                          .tz(userTimezone)
                                                          .locale(locale)
                                                          .format("DD MMM"),
                                                  })}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="btn-3d lip-orange bg-primary hover:bg-primary/90 text-white px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200 shrink-0 hover:scale-105 lg:block hidden">
                            {progress > 0 ? t("continue") : t("start")}
                        </div>
                    </div>

                    <div className="w-full bg-gray-200 dark:bg-grayDark-700 rounded-full h-2 overflow-hidden relative">
                        <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                                isCompleted
                                    ? "bg-green-500"
                                    : isOverdue
                                    ? "bg-red-500"
                                    : "bg-primary"
                            }`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
};

// Composant pour les assignements secondaires
const SecondaryAssignmentCard = ({ assignment, userTimezone }) => {
    const t = useTranslations("AssignmentSection");
    const locale = useLocale();
    const course = assignment.course;
    const team = assignment.team;
    const dueDate = assignment.dueDate;
    const progress = assignment.progress || 0;
    const partNumber = course?.partNumber || 1;
    const sessionPosition = course?.sessionPosition || 1;

    const isOverdue =
        dueDate && progress < 100 && moment().tz(userTimezone).isAfter(moment(dueDate));
    const isDueSoon =
        dueDate &&
        !isOverdue &&
        progress < 100 &&
        moment(dueDate).diff(moment().tz(userTimezone), "days") <= 3;
    const isCompleted = progress === 100;

    return (
        <Link href={`/course/${course?.slug}/${partNumber}/${sessionPosition}`} className="group block">
            <Card className={`special-card ${isCompleted ? "overflow-hidden" : ""}`}>
                <CardContent className="pt-5 relative">
                    {isCompleted && (
                        <div
                            className="absolute inset-0 pointer-events-none opacity-[0.03]"
                            style={{
                                backgroundImage: "repeating-linear-gradient(45deg, currentColor, currentColor 1px, transparent 1px, transparent 8px)"
                            }}
                        />
                    )}
                    <div className="flex items-center gap-3 relative">
                        <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                            {course?.image ? (
                                <Image
                                    src={course.image}
                                    alt={course?.name || t("unknownCourse")}
                                    width={48}
                                    height={48}
                                    className="object-cover w-full h-full"
                                />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">
                                        {course?.name?.charAt(0) || "?"}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <h5 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight truncate group-hover:text-black group-hover:dark:text-white transition-colors">
                                    {course?.name || t("unknownCourse")}
                                </h5>
                                {dueDate && (
                                    <span
                                        className={`text-xs px-1.5 py-0.5 rounded-md font-medium shrink-0 ${
                                            isOverdue
                                                ? "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400"
                                                : isDueSoon
                                                ? "bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400"
                                                : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-grayDark-300"
                                        }`}
                                    >
                                        {isOverdue
                                            ? t("late")
                                            : moment(dueDate)
                                                  .tz(userTimezone)
                                                  .locale(locale)
                                                  .format("DD MMM")}
                                    </span>
                                )}
                            </div>
                            {/* Team info */}
                            <div className="flex items-center gap-1.5 mt-1">
                                {team?.logo ? (
                                    <Image
                                        src={team.logo}
                                        alt={team.name}
                                        width={14}
                                        height={14}
                                        className="rounded object-cover"
                                    />
                                ) : (
                                    <div className="w-3.5 h-3.5 bg-primary/20 rounded flex items-center justify-center">
                                        <span className="text-[7px] font-bold text-primary">
                                            {team?.name?.charAt(0)?.toUpperCase() || "T"}
                                        </span>
                                    </div>
                                )}
                                <span className="text-xs text-gray-400 dark:text-grayDark-500">
                                    {team?.name}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1.5">
                                <div className="flex-1 bg-gray-200 dark:bg-grayDark-700 rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className={`h-1.5 rounded-full transition-all duration-300 ${
                                            isCompleted
                                                ? "bg-green-500"
                                                : isOverdue
                                                ? "bg-red-500"
                                                : "bg-primary"
                                        }`}
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <span className="text-sm text-gray-500 dark:text-grayDark-400 font-medium shrink-0 min-w-[35px]">
                                    {progress}%
                                </span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
};

// État vide
const EmptyState = () => {
    const t = useTranslations("AssignmentSection");
    return (
        <div className="bg-white dark:bg-bgDark-840 rounded-lg border border-gray-200 dark:border-borderDark p-6 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg
                    className="w-6 h-6 text-gray-400 dark:text-grayDark-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                </svg>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-base mb-2">
                {t("noAssignments")}
            </h3>
            <p className="text-gray-600 dark:text-grayDark-400 text-sm mb-0">
                {t("noAssignmentsDescription")}
            </p>
        </div>
    );
};

// Composant principal
export default function AssignmentSection({ assignments, userTimezone }) {
    const [showAll, setShowAll] = useState(false);
    const t = useTranslations("AssignmentSection");

    if (!assignments?.length) {
        return null; // Ne pas afficher la section si pas d'assignements
    }

    const mainAssignment = assignments[0];
    const remainingAssignments = assignments.slice(1);
    const visibleSecondaryAssignments = showAll
        ? remainingAssignments
        : remainingAssignments.slice(0, 2);
    const hasMoreAssignments = remainingAssignments.length > 2;

    // Compter le nombre d'équipes uniques
    const uniqueTeams = new Set(assignments.map((a) => a.team?.id).filter(Boolean));
    const teamsCount = uniqueTeams.size;

    return (
        <div className="mt-12 lg:mt-14">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <h2 className="font-bold text-gray-900 dark:text-white text-xl">
                        {assignments.length > 1
                            ? t("yourAssignments")
                            : t("yourAssignment")}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                        {t("assignmentsSubtitle", {
                            assignmentsCount: assignments.length,
                            teamsCount: teamsCount,
                        })}
                    </p>
                </div>
                {assignments.length > 1 && (
                    <span className="text-sm text-gray-500 dark:text-grayDark-400">
                        {t("assignmentsNumber", {
                            number: assignments.length,
                        })}
                    </span>
                )}
            </div>

            <div className="lg:space-y-6 space-y-3">
                <MainAssignmentCard
                    assignment={mainAssignment}
                    userTimezone={userTimezone}
                />

                {visibleSecondaryAssignments.length > 0 && (
                    <div className="xl:space-y-3 space-y-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {visibleSecondaryAssignments.map(
                                (assignment, index) => (
                                    <SecondaryAssignmentCard
                                        key={`assignment-${
                                            assignment.id || index
                                        }-${assignment.course?.id || "unknown"}`}
                                        assignment={assignment}
                                        userTimezone={userTimezone}
                                    />
                                )
                            )}
                        </div>

                        {hasMoreAssignments && (
                            <div className="flex justify-center pt-2">
                                <button
                                    onClick={() => setShowAll(!showAll)}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-grayDark-400 hover:text-primary dark:hover:text-primary transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-grayDark-800 rounded-lg"
                                >
                                    {showAll ? (
                                        <>
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 15l7-7 7 7"
                                                />
                                            </svg>
                                            {t("seeLess")}
                                        </>
                                    ) : (
                                        <>
                                            <span>
                                                {t("seeMoreWithNumber", {
                                                    number:
                                                        remainingAssignments.length -
                                                        2,
                                                })}
                                            </span>
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
